import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GATEWAY_LABELS } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { usePayReturn } from '../hooks/usePayReturn';
import { usePayment } from '../hooks/usePayment';
import { useMyOrder } from '../hooks/useMyOrders';
import { money } from '../utils/money';
import { Button, PageHeader } from '../components/ui';
import {
  ConfirmSheet,
  ConfirmingCard,
  MethodPickerSheet,
  PaymentFailedCard,
  QrSheet,
} from '../components/Payment';

/**
 * /pay/return (W11): where eSewa, Khalti, IME Pay, ConnectIPS (and Razorpay redirects) send the
 * customer back. Shows "Confirming your payment…" while the server checks with the gateway, then
 * opens the order when paid, or a failure card with "Try again" (another way) and "Cancel order".
 */
export function PayReturnPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const r = usePayReturn(location.search);
  const orderId = r.orderId ?? '';
  const o = useMyOrder(r.status === 'failed' ? orderId : '');
  const p = usePayment(
    orderId,
    {
      paid: t('payment.paid'),
      notFinished: t('payment.notFinished'),
      notYet: t('payment.notYet'),
      failed: t('payment.failed'),
      cancelled: t('payment.cancelled'),
      copied: t('payment.qrCopied'),
      gatewayDown: t('payment.gatewayDown'),
    },
    {
      onPaid: (intent) => navigate(`/orders/${intent.orderId}?placed=1`, { replace: true }),
      onCancelled: () => navigate(`/orders/${orderId}`, { replace: true }),
    },
  );

  // Paid: straight to the order (history replaced so Back does not re-run the check).
  useEffect(() => {
    if (r.status === 'paid' && r.orderId) {
      navigate(`/orders/${r.orderId}?placed=1`, { replace: true });
    }
  }, [r.status, r.orderId, navigate]);

  const gateway = r.gateway ? GATEWAY_LABELS[r.gateway] : '';
  const order = o.order;
  const stillWaiting = order?.stage === 'awaiting_payment';
  const methodRows = (order?.payment.retryMethods ?? []).map((method) => ({
    method,
    name: t(`pay.method.${method}`),
    available: true,
    caption: null,
  }));

  return (
    <div className="min-h-dvh bg-page pb-10">
      <PageHeader
        title={t('payment.returnTitle')}
        onBack={() => navigate('/', { replace: true })}
        backLabel={t('common.back')}
      />
      <div className="flex flex-col gap-5 px-4 pt-5">
        {r.status === 'checking' || r.status === 'paid' ? (
          <ConfirmingCard
            title={t('payment.confirming')}
            body={t('payment.confirmingBody', { gateway })}
            slowNote={r.slow ? t('payment.slow') : null}
            action={
              r.slow && r.orderId ? (
                <Button tone="outline" onClick={() => navigate(`/orders/${r.orderId}`)}>
                  {t('payment.viewOrder')}
                </Button>
              ) : undefined
            }
          />
        ) : r.status === 'failed' ? (
          <PaymentFailedCard
            title={t('payment.failed')}
            body={r.intent?.failure ?? t('payment.failedBody')}
          >
            {stillWaiting && methodRows.length > 0 && (
              <Button full busy={p.retrying} onClick={p.openPicker}>
                {t('payment.tryAgain')}
              </Button>
            )}
            {stillWaiting && (
              <Button full tone="danger" busy={p.abandoning} onClick={p.askCancel}>
                {t('payment.cancelOrder')}
              </Button>
            )}
            {r.orderId && (
              <Button full tone="outline" onClick={() => navigate(`/orders/${r.orderId}`)}>
                {t('payment.viewOrder')}
              </Button>
            )}
          </PaymentFailedCard>
        ) : (
          <PaymentFailedCard
            title={t('payment.returnBroken')}
            body={r.status === 'error' ? r.error : t('payment.returnBrokenBody')}
          >
            {r.status === 'error' && (
              <Button full onClick={r.retry}>
                {t('common.retry')}
              </Button>
            )}
            <Button full tone="outline" onClick={() => navigate('/account/orders')}>
              {t('orders.title')}
            </Button>
          </PaymentFailedCard>
        )}
      </div>

      {p.pickerOpen && (
        <MethodPickerSheet
          title={t('payment.pickMethod')}
          rows={methodRows}
          busyMethod={p.retryingMethod}
          onPick={p.retry}
          onClose={p.closePicker}
        />
      )}
      {p.qr && order && (
        <QrSheet
          title={t('payment.qrTitle', { amount: money(order.country, p.qr.amountMinor) })}
          body={t('payment.qrBody')}
          payload={p.qr.payload}
          labels={{
            copy: t('payment.qrCopy'),
            waiting: t('payment.qrWaiting'),
            paid: t('payment.qrPaid'),
          }}
          checking={p.checking}
          onCopy={p.copyQr}
          onPaid={p.checkQr}
          onClose={p.closeQr}
        />
      )}
      {p.confirmCancel && (
        <ConfirmSheet
          title={t('payment.cancelTitle')}
          body={t('payment.cancelBody')}
          yesLabel={t('payment.cancelOrder')}
          noLabel={t('payment.keepOrder')}
          busy={p.abandoning}
          onYes={p.abandon}
          onNo={p.keepOrder}
        />
      )}
    </div>
  );
}
